import { Button } from '@mui/material';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import './RightPanel.css';
import { useRef } from 'react';
import i18n from '../../initializers/i18n';
import { useAppTheme } from '../../theme/AppThemeContext';
export type PanelType = 'top' | 'right' | 'bottom' | 'left';

type Nullable<T> = T | null;
export interface SliderProps {
  type?: PanelType;
  size?: number;
  panelContainerClassName?: string;
  panelClassName?: string;
  isOpen: boolean;
  children?: Nullable<React.ReactElement>;
  noBackdrop?: boolean;
  noButtons?: boolean;
  backdropClicked?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onOpen?: (node: HTMLElement, isAppearing: boolean) => void;
  onOpening?: (node: HTMLElement, isAppearing: boolean) => void;
  onOpened?: (node: HTMLElement, isAppearing: boolean) => void;
  onClose?: (node: HTMLElement) => void;
  onCancel?: () => void;
  onConfirm?: () => void;
  onKill?: () => void;
  onClosing?: (node: HTMLElement) => void;
  onClosed?: (node: HTMLElement) => void;
}

type PanelGlassStyle = {
  width: string;
  height: string;
  left?: number;
  right?: number;
  bottom?: number;
  top?: number | string;
  overflow?: string;
  position:
    | 'inherit'
    | '-moz-initial'
    | 'initial'
    | 'revert'
    | 'unset'
    | '-webkit-sticky'
    | 'absolute'
    | 'fixed'
    | 'relative'
    | 'static'
    | 'sticky'
    | undefined;
};

const getPanelGlassStyle = (type: PanelType, size: number, hidden: boolean): PanelGlassStyle => {
  const horizontal = type === 'bottom' || type === 'top';
  return {
    width: horizontal ? `${hidden ? '0' : '100'}vw` : `${100 - size}vw`,
    height: horizontal ? `${100 - size}vh` : `${hidden ? '0' : 'calc(100dvh - var(--app-top-inset, 0px))'}`,
    ...(!horizontal && { top: 'var(--app-top-inset, 0px)' }),
    ...(type === 'right' && { left: 0 }),
    ...(type === 'top' && { bottom: 0 }),
    position: 'fixed',
  };
};

const getPanelStyle = (type: PanelType, size: number): PanelGlassStyle => {
  const horizontal = type === 'bottom' || type === 'top';
  return {
    width: horizontal ? '100vw' : `${size}vw`,
    height: horizontal ? `${size}vh` : 'calc(100dvh - var(--app-top-inset, 0px))',
    top: horizontal ? 0 : 'var(--app-top-inset, 0px)',
    ...(type === 'right' && { right: 0 }),
    ...(type === 'bottom' && { bottom: 0 }),
    position: 'fixed',
    overflow: 'auto',
  };
};

const RightPanel: React.FunctionComponent<SliderProps> = ({
  type = 'left',
  size = 50,
  panelContainerClassName = '',
  panelClassName = '',
  isOpen,
  onOpen,
  onCancel,
  onConfirm,
  onKill,
  onOpening,
  onOpened,
  onClose,
  onClosing,
  onClosed,
  backdropClicked,
  noBackdrop = false,
  noButtons,
  children = null,
}) => {
  const panelRef = useRef<HTMLDivElement>(null); // Use a ref for the panel
  const { theme } = useAppTheme();

  const glassBefore = type === 'right' || type === 'bottom';
  const horizontal = type === 'bottom' || type === 'top';

  const panelMarkup = (
    <div className={`bcTheme-${theme}`}>
      <div className={`right-panel-container ${isOpen ? 'active' : ''} ${noBackdrop ? 'click-through' : ''}`}>
        <CSSTransition
          in={isOpen}
          timeout={500}
          classNames={`panel-container-${type}`}
          unmountOnExit
          nodeRef={panelRef} // Attach the ref
          onEnter={() => onOpen}
          onEntering={() => onOpening}
          onEntered={() => onOpened}
          onExit={() => onClose}
          onExiting={() => onClosing}
          onExited={() => onClosed}
          style={{ display: horizontal ? 'block' : 'flex' }}
        >
          <div ref={panelRef}>
            {glassBefore && (
              <div
                className="glass"
                style={getPanelGlassStyle(type, size, !!noBackdrop)}
                onClick={(e) => {
                  if (!noBackdrop && backdropClicked) backdropClicked(e);
                }}
              />
            )}
            <div className={`panel ${panelContainerClassName || ''}`} style={getPanelStyle(type, size)}>
              <div className={`panel-content ${panelClassName || ''}`}>
                <div className="close-btn-container">
                  <button type="button" className="close-button" onClick={onKill}>
                    X
                  </button>
                </div>

                {children}
                {noButtons ? (
                  <div className="panel_add_footer">
                    <div>
                      <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} onClick={onConfirm}>
                        {i18n.t('actions.confirm')}
                      </Button>
                    </div>
                    <div>
                      <Button color="warning" type="reset" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} onClick={onCancel}>
                        {i18n.t('actions.cancel')}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            {!glassBefore && (
              <div
                className="glass"
                style={getPanelGlassStyle(type, size, !!noBackdrop)}
                onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                  if (!noBackdrop && backdropClicked) backdropClicked(e);
                }}
              />
            )}
          </div>
        </CSSTransition>
      </div>
    </div>
  );

  return createPortal(panelMarkup, document.body);
};

export default RightPanel;
